export const generateLoadShareableLink = (loadId) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/loads/${loadId}`;
};

/**
 * Get the corrected server time using the synced offset from WebSocket.
 * This allows accurate time calculations without relying on client clock accuracy.
 * 
 * @returns {Date} Current time adjusted by server offset
 */
export const getServerCorrectedNow = () => {
  const offset = window.__serverTimeOffset || 0;
  return new Date(Date.now() + offset);
};

/**
 * Calculate time remaining until a target time (using server-corrected time).
 * 
 * @param {string|Date} targetTime - Target time (ISO string or Date)
 * @returns {object} { expired, hours, minutes, seconds, totalMs }
 */
export const getTimeRemaining = (targetTime) => {
  if (!targetTime) return { expired: false, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };
  
  const target = new Date(targetTime);
  if (isNaN(target.getTime())) {
    return { expired: false, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };
  }
  
  const now = getServerCorrectedNow();
  const totalMs = target.getTime() - now.getTime();
  
  if (totalMs <= 0) {
    return { expired: true, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };
  }
  
  const hours = Math.floor(totalMs / (1000 * 60 * 60));
  const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);
  
  return { expired: false, hours, minutes, seconds, totalMs };
};

/**
 * Determine if a load is expired based on server-computed expiry status.
 * 
 * IMPORTANT: This function prioritizes server-computed `is_expired` flag.
 * The server uses canonical UTC timestamps (expires_at_utc) for accurate
 * timezone-independent expiry determination. Client time is NEVER authoritative.
 * 
 * Priority order:
 * 1. Server-computed `is_expired` field (most accurate, authoritative)
 * 2. Server-computed `expiresAtUtc` vs serverNowUtc (if available)
 * 3. Fallback: pickup_datetime comparison (legacy, less accurate)
 * 
 * @param {object} load - The load object from the API
 * @returns {boolean} True if the load is expired, false otherwise
 */
export const isLoadExpired = (load) => {
  if (!load) return false;
  
  // Only open loads can be "expired" in the bidding sense
  const status = (load.status || '').toLowerCase();
  if (status !== 'open') return false;
  
  // PREFERRED: Use server-computed is_expired flag (authoritative)
  // This is computed server-side using canonical UTC timestamps
  if (typeof load.is_expired === 'boolean') {
    return load.is_expired;
  }
  
  // SECONDARY: Use expiresAtUtc from server response
  // Compare against serverNowUtc if available, else use client time (with offset if synced)
  if (load.expiresAtUtc) {
    try {
      const expiresAt = new Date(load.expiresAtUtc);
      if (!isNaN(expiresAt.getTime())) {
        // Use server time if available, otherwise fall back to client time
        const now = load.serverNowUtc 
          ? new Date(load.serverNowUtc) 
          : new Date();
        return now >= expiresAt;
      }
    } catch (e) {
      // Fall through to legacy fallback
    }
  }
  
  // LEGACY FALLBACK: Use pickup_datetime (ISO8601 with timezone)
  // This is less accurate because it relies on client time
  if (load.pickup_datetime) {
    try {
      const pickupDateTime = new Date(load.pickup_datetime);
      if (!isNaN(pickupDateTime.getTime())) {
        return pickupDateTime < new Date();
      }
    } catch (e) {
      // Fall through to date+time fallback
    }
  }
  
  // LAST RESORT: Use pickup_date + pickup_time
  // This assumes the time is in the user's local timezone (least accurate)
  if (load.pickup_date) {
    const dateParts = load.pickup_date.split('-');
    if (dateParts.length === 3) {
      const pickupDateTime = new Date(
        parseInt(dateParts[0]),      // year
        parseInt(dateParts[1]) - 1,  // month (0-indexed)
        parseInt(dateParts[2])       // day
      );
      
      if (load.pickup_time) {
        const timeParts = load.pickup_time.split(':');
        const hours = parseInt(timeParts[0]) || 0;
        const minutes = parseInt(timeParts[1]) || 0;
        pickupDateTime.setHours(hours, minutes, 0, 0);
      }
      
      return pickupDateTime < new Date();
    }
  }
  
  // If we can't determine, assume not expired
  return false;
};

/**
 * Format a UUID for display to users
 * @param {string} uuid - The full UUID string
 * @param {string} format - 'short' (first 8 chars), 'medium' (first 12 chars), or 'full'
 * @returns {string} Formatted UUID
 */
export const formatUuid = (uuid, format = 'short') => {
  if (!uuid) return 'N/A';
  
  const cleanUuid = uuid.toUpperCase();
  
  switch (format) {
    case 'short':
      // First segment only: e.g., "A1B2C3D4"
      return cleanUuid.split('-')[0];
    case 'medium':
      // First two segments: e.g., "A1B2C3D4-E5F6"
      const parts = cleanUuid.split('-');
      return `${parts[0]}-${parts[1]}`;
    case 'full':
    default:
      return cleanUuid;
  }
};

/**
 * Get the display identifier for a load (UUID preferred, falls back to ID)
 * @param {object} load - The load object
 * @param {string} format - 'short', 'medium', or 'full' for UUID format
 * @returns {string} Display identifier
 */
export const getLoadDisplayId = (load, format = 'short') => {
  if (!load) return 'N/A';
  
  // Prefer UUID if available
  if (load.uuid) {
    return formatUuid(load.uuid, format);
  }
  
  // Fallback to integer ID for legacy data
  return load.id?.toString() || 'N/A';
};

/**
 * Get the identifier to use in URLs/API calls (UUID preferred)
 * @param {object} load - The load object
 * @returns {string} URL-safe identifier
 */
export const getLoadUrlId = (load) => {
  if (!load) return null;
  return load.uuid || load.id?.toString();
};

// Compute the first pickup details, preferring stop data over legacy load-level fields
export const computeFirstPickup = (load) => {
  // Determine if there is an accepted alternative bid to prefer its schedule
  const acceptedAltBid = (() => {
    const bids = load?.bids || [];
    if (!Array.isArray(bids) || bids.length === 0) return null;
    return bids.find((b) => {
      const status = (b.status || '').toString().toLowerCase();
      const type = (b.bid_type || b.bidType || '').toString().toLowerCase();
      return status === 'accepted' && type === 'alternative';
    }) || null;
  })();

  if (load && Array.isArray(load.stops) && load.stops.length > 0) {
    const sortedStops = [...load.stops].sort((a, b) => a.sequence - b.sequence);
    const pickupStops = sortedStops.filter((s) => s.stop_type === 'pickup');
    const firstPickup = pickupStops.length > 0 ? pickupStops[0] : sortedStops[0];

    return {
      // Precedence: accepted alt bid -> load-level overrides -> stop data
      date: (acceptedAltBid?.alt_pickup_date || acceptedAltBid?.altPickupDate) 
        || load.pickup_date 
        || firstPickup.date 
        || null,
      time: (acceptedAltBid?.alt_pickup_time || acceptedAltBid?.altPickupTime) 
        || load.pickup_time 
        || firstPickup.time 
        || null,
      city: firstPickup.city || load.origin_city || null,
      state: firstPickup.state || load.origin_state || null,
      address: firstPickup.address || load.origin_address || null,
      zip: firstPickup.zip || load.origin_zip || null,
    };
  }

  return {
    date: (acceptedAltBid?.alt_pickup_date || acceptedAltBid?.altPickupDate) || load?.pickup_date || null,
    time: (acceptedAltBid?.alt_pickup_time || acceptedAltBid?.altPickupTime) || load?.pickup_time || null,
    city: load?.origin_city || null,
    state: load?.origin_state || null,
    address: load?.origin_address || null,
    zip: load?.origin_zip || null,
  };
};

// Compute the last delivery details, preferring stop data over legacy load-level fields
export const computeLastDelivery = (load) => {
  // Determine if there is an accepted alternative bid to prefer its schedule
  const acceptedAltBid = (() => {
    const bids = load?.bids || [];
    if (!Array.isArray(bids) || bids.length === 0) return null;
    return bids.find((b) => {
      const status = (b.status || '').toString().toLowerCase();
      const type = (b.bid_type || b.bidType || '').toString().toLowerCase();
      return status === 'accepted' && type === 'alternative';
    }) || null;
  })();

  if (load && Array.isArray(load.stops) && load.stops.length > 0) {
    const sortedStops = [...load.stops].sort((a, b) => a.sequence - b.sequence);
    const deliveryStops = sortedStops.filter((s) => s.stop_type === 'delivery');
    const lastDelivery = deliveryStops.length > 0 
      ? deliveryStops[deliveryStops.length - 1] 
      : sortedStops[sortedStops.length - 1];

    return {
      // Precedence: accepted alt bid -> load-level overrides -> stop data
      date: (acceptedAltBid?.alt_delivery_date || acceptedAltBid?.altDeliveryDate)
        || load.delivery_date 
        || lastDelivery.date 
        || null,
      time: (acceptedAltBid?.alt_delivery_time || acceptedAltBid?.altDeliveryTime)
        || load.delivery_time 
        || lastDelivery.time 
        || null,
      city: lastDelivery.city || load.destination_city || null,
      state: lastDelivery.state || load.destination_state || null,
      address: lastDelivery.address || load.destination_address || null,
      zip: lastDelivery.zip || load.destination_zip || null,
    };
  }

  return {
    date: (acceptedAltBid?.alt_delivery_date || acceptedAltBid?.altDeliveryDate) || load?.delivery_date || null,
    time: (acceptedAltBid?.alt_delivery_time || acceptedAltBid?.altDeliveryTime) || load?.delivery_time || null,
    city: load?.destination_city || null,
    state: load?.destination_state || null,
    address: load?.destination_address || null,
    zip: load?.destination_zip || null,
  };
};